import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { get } from "lodash";
import {
  Button,
  Grid,
  Typography,
  Stack,
  Divider,
  Chip,
  MenuItem,
  Alert,
  AlertTitle,
} from "@mui/material";
import { color } from "../../utils/constant";
import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import { useSelector } from "react-redux";
import { RHFSelect } from "../../components/shared/RHFSelect";
import { useDoctorAvailabilityForm } from "../../data/talons/DoctorAvailability/useDoctorAvailabilityForm";
import {
  getDoctorAvailabilityData,
  getWeekDayList,
  getDoctorList,
  getAreaList,
} from "../../data/selectors/DoctorAvailability/doctorAvailabilityForm.selector";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";

export default function DoctorAvailabilityForm(props) {
  const editItem = useSelector(getDoctorAvailabilityData);
  const doctorList = useSelector(getDoctorList);
  const areaList = useSelector(getAreaList);
  const weekDayList = useSelector(getWeekDayList);
  const [Err, setErr] = useState(false);

  const {
    fetchWeekDayList,
    weekDay,
    DoctorAvailabilityEntrySchema,
    DoctorId,
    AreaId,
    fetchAreaComboList,
    findAreaFromLabel,
    handleWeekDayChange,
    findDoctorFromLabel,
    DoctorAvailabilityEntry,
    DoctorAvailabilityUpdate,
    morningFromTime,
    handleDateChange,
    fetchDoctorComboList,
    areaWarning,
    setAreaWarning,
  } = useDoctorAvailabilityForm({
    existingWeekDay: get(editItem, "week_day", 0),
    existingDoctorName: get(editItem, "doctor_id", ""),
    existingAreaName: get(editItem, "area_id", ""),
    existingMorningFromTime: new Date(
      get(editItem, "morning_from_time", format(new Date(), "HH:mm"))
    ),
    existingMorningUptoTime: new Date(
      get(editItem, "morning_upto_time", format(new Date(), "HH:mm"))
    ),
    existingEveningFromTime: new Date(
      get(editItem, "evening_from_time", format(new Date(), "HH:mm"))
    ),
    existingEveningUptoTime: new Date(
      get(editItem, "evening_upto_time", format(new Date(), "HH:mm"))
    ),
  });

  useEffect(() => {
    fetchDoctorComboList();
    fetchWeekDayList();
    fetchAreaComboList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultValues = useMemo(
    () => ({
      name: get(editItem, "doctor_name", ""),
      area_name: get(editItem, "area_name", ""),
      week_day: String(get(editItem, "week_day", "")),
      morning_from_time: get(editItem, "morning_from_time", ""),
      morning_upto_time: get(editItem, "morning_upto_time", ""),
      evening_from_time: get(editItem, "evening_from_time", ""),
      evening_upto_time: get(editItem, "evening_upto_time", ""),
    }),
    [editItem]
  );

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    setValue,
    setError,
  } = useForm({
    resolver: joiResolver(DoctorAvailabilityEntrySchema),
    defaultValues,
  });

  const onUserSubmit = useCallback(async () => {
    try {
      const vals = getValues();

      const mf = vals.morning_from_time || ""; // HH:mm
      const mt = vals.morning_upto_time || "";
      const ef = vals.evening_from_time || "";
      const et = vals.evening_upto_time || "";

      // OPTIONAL: period fields if you have them in UI
      const fromPeriod =
        vals.date_from_period || vals.from_period || vals.from_session; // 'Morning' | 'Evening'
      const toPeriod = vals.date_to_period || vals.to_period || vals.to_session; // 'Morning' | 'Evening'

      const hasMorning = Boolean(mf && mt);
      const hasEvening = Boolean(ef && et);

      const fail = (message, fields = []) => {
        enqueueSnackbar(message, { variant: "error" });
        fields.forEach((f) => setError(f, { type: "manual", message }));
      };

      // ---- 0) Exactly 3 time fields filled => identify the missing one and require it
      const timeFields = [
        { key: "morning_from_time", val: mf, label: "Morning From Time" },
        { key: "morning_upto_time", val: mt, label: "Morning Upto Time" },
        { key: "evening_from_time", val: ef, label: "Evening From Time" },
        { key: "evening_upto_time", val: et, label: "Evening Upto Time" },
      ];
      const filledCount = timeFields.filter((f) => !!f.val).length;
      if (filledCount === 3) {
        const missing = timeFields.find((f) => !f.val);
        return fail(`${missing.label} is required.`, [missing.key]);
      }

      // ---- 2) At least one complete slot required
      if (!hasMorning && !hasEvening) {
        const spansBoth = fromPeriod === "Morning" && toPeriod === "Evening";
        const msg = spansBoth
          ? "Please enter at least one time slot (Morning or Evening)."
          : "Please enter a Morning or Evening time slot.";
        return fail(msg, ["morning_from_time", "evening_from_time"]);
      }

      const res =
        get(editItem, "id", null) === null
          ? await DoctorAvailabilityEntry({
              ...getValues(),
            })
          : await DoctorAvailabilityUpdate({
              ...getValues(),
              id: get(editItem, "id", null),
            });

      if (res === true) {
        props.postSubmit();
      }
    } catch (err) {
      console.log("errr", err);
    }
  }, [
    DoctorAvailabilityEntry,
    DoctorAvailabilityUpdate,
    editItem,
    getValues,
    props,
    setError,
  ]);

  return (
    <React.Fragment>
      <Typography variant="h5" gutterBottom>
        Doctor Availability Form
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {areaWarning && (
        <Alert
          severity="error"
          onClose={() => setAreaWarning(null)}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: '1px solid #f44336',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
            ⚠️ Cannot Assign Availability
          </AlertTitle>
          {areaWarning.message}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="name"
            label="Doctor Name"
            freeSolo
            data={doctorList || []}
            defaultValue={DoctorId}
            onInputChange={(value) => {
              setAreaWarning(null);
              findDoctorFromLabel(value);
            }}
            id={doctorList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="area_name"
            label="Area Name"
            freeSolo
            data={areaList || []}
            defaultValue={AreaId}
            onInputChange={(value) => {
              setAreaWarning(null);
              findAreaFromLabel(value);
            }}
            id={areaList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item sm={12} xs={12}>
          <RHFSelect
            required
            // native
            name="week_day"
            label="Week Day"
            method={control}
            defaultValue={weekDay}
          >
            {weekDayList?.map((item) => (
              <MenuItem
                key={item?.id}
                label={item?.label}
                value={item?.id}
                onClick={() => handleWeekDayChange(item.id)}
              >
                {item?.label}
              </MenuItem>
            ))}
          </RHFSelect>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="morning_from_time"
              control={control}
              rules={{ required: "Morning From Time is required" }}
              render={({ field: { onChange, onBlur, value, name } }) => (
                <TimePicker
                  id={name}
                  label="Morning From Time"
                  value={value ? dayjs(value, "HH:mm") : null}
                  onChange={(newValue) => {
                    const uptoTime = getValues("morning_upto_time");
                    const formattedTime = newValue
                      ? newValue.format("HH:mm:ss")
                      : "";
                    if (
                      uptoTime &&
                      newValue &&
                      dayjs(formattedTime, "HH:mm:ss").isAfter(
                        dayjs(uptoTime, "HH:mm:ss")
                      )
                    ) {
                      enqueueSnackbar(
                        "From Time cannot be later than Upto Time",
                        { variant: "error" }
                      );
                      setErr(true);
                      return;
                    } else {
                      setErr(false);
                    }
                    onChange(formattedTime);
                  }}
                  onBlur={onBlur}
                  slotProps={{ textField: { fullWidth: true } }}
                  ampm={true} // Show AM/PM
                  // Restricting morning times to AM
                  minTime={dayjs().set("hour", 0).set("minute", 0)} // Start at midnight
                  maxTime={dayjs().set("hour", 11).set("minute", 59)} // End at 11:59 AM
                />
              )}
            />
            {/* Error handling */}
            {errors.morning_from_time && (
              <span style={{ color: "#d32f2f", fontSize: "0.75rem" }}>
                {errors.morning_from_time.message}
              </span>
            )}
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="morning_upto_time"
              control={control}
              rules={{ required: "Morning Upto Time is required" }}
              render={({ field: { onChange, onBlur, value, name } }) => (
                <TimePicker
                  id={name}
                  label="Morning Upto time"
                  value={value ? dayjs(value, "HH:mm") : null}
                  onChange={(newValue) => {
                    const fromTime = getValues("morning_from_time");

                    const formattedTime = newValue
                      ? newValue.format("HH:mm:ss")
                      : "";
                    if (
                      fromTime &&
                      newValue &&
                      dayjs(formattedTime, "HH:mm:ss").isBefore(
                        dayjs(fromTime, "HH:mm:ss")
                      )
                    ) {
                      enqueueSnackbar(
                        "Upto Time cannot be earlier than From Time",
                        {
                          variant: "error",
                        }
                      );
                      setErr(true);
                      return;
                    } else {
                      setErr(false);
                    }
                    onChange(formattedTime);
                  }}
                  onBlur={onBlur}
                  slotProps={{ textField: { fullWidth: true } }}
                  ampm={true} // Show AM/PM
                  // Restricting morning times to AM
                  minTime={dayjs().set("hour", 0).set("minute", 0)} // Start at midnight
                  maxTime={dayjs().set("hour", 11).set("minute", 59)} // End at 11:59 AM
                />
              )}
            />

            {errors.morning_upto_time && (
              <span style={{ color: "#d32f2f", fontSize: "0.75rem" }}>
                {errors.morning_upto_time.message}
              </span>
            )}
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="evening_from_time"
              control={control}
              rules={{ required: "Evening From Time is required" }}
              render={({ field: { onChange, onBlur, value, name } }) => (
                <TimePicker
                  id={name}
                  label="Evening From Time"
                  value={value ? dayjs(value, "HH:mm") : null}
                  onChange={(newValue) => {
                    const uptoTime = getValues("evening_upto_time");
                    const formattedTime = newValue
                      ? newValue.format("HH:mm:ss")
                      : "";
                    if (
                      uptoTime &&
                      newValue &&
                      dayjs(formattedTime, "HH:mm:ss").isAfter(
                        dayjs(uptoTime, "HH:mm:ss")
                      )
                    ) {
                      enqueueSnackbar(
                        "From Time cannot be later than Upto Time",
                        { variant: "error" }
                      );
                      setErr(true);
                      return;
                    } else {
                      setErr(false);
                    }

                    onChange(formattedTime); // Update form state
                  }}
                  onBlur={onBlur}
                  slotProps={{ textField: { fullWidth: true } }}
                  ampm={true}
                  referenceDate={dayjs().startOf("day").hour(18)}
                  minTime={dayjs().startOf("day").hour(12)} // 12:00 PM
                  maxTime={dayjs().startOf("day").hour(23).minute(59)} // 11:59 PM
                  views={["hours", "minutes"]}
                />
              )}
            />
            {/* Error handling */}
            {errors.evening_from_time && (
              <span style={{ color: "#d32f2f", fontSize: "0.75rem" }}>
                {errors.evening_from_time.message}
              </span>
            )}
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="evening_upto_time"
              control={control}
              rules={{ required: "Evening Upto Time is required" }}
              render={({ field: { onChange, onBlur, value, name } }) => (
                <TimePicker
                  id={name}
                  label="Evening Upto Time"
                  value={value ? dayjs(value, "HH:mm") : null}
                  onChange={(newValue) => {
                    const formattedTime = newValue
                      ? newValue.format("HH:mm:ss")
                      : "";

                    const fromTime = getValues("evening_from_time");
                    if (
                      fromTime &&
                      newValue &&
                      dayjs(formattedTime, "HH:mm:ss").isBefore(
                        dayjs(fromTime, "HH:mm:ss")
                      )
                    ) {
                      enqueueSnackbar(
                        "Upto Time cannot be earlier than From Time",
                        {
                          variant: "error",
                        }
                      );
                      setErr(true);
                      return;
                    } else {
                      setErr(false);
                    }

                    onChange(formattedTime); // Update form state
                  }}
                  onBlur={onBlur}
                  slotProps={{ textField: { fullWidth: true } }}
                  ampm={true}
                  referenceDate={dayjs().startOf("day").hour(18)}
                  minTime={dayjs().startOf("day").hour(12)} // 12:00 PM
                  maxTime={dayjs().startOf("day").hour(23).minute(59)} // 11:59 PM
                  views={["hours", "minutes"]}
                />
              )}
            />
            {/* Error handling */}
            {errors.evening_upto_time && (
              <span style={{ color: "#d32f2f", fontSize: "0.75rem" }}>
                {errors.evening_upto_time.message}
              </span>
            )}
          </LocalizationProvider>
        </Grid>

        <Grid container direction="row">
          <Grid item xs={12} sm={12}>
            <Stack direction="row" justifyContent="end">
              <Button
                variant="contained"
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  backgroundColor: color.button,
                  "&:hover": {
                    backgroundColor: color.button,
                  },
                }}
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(onUserSubmit)();
                }}
              >
                Save
              </Button>
              <Button
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  borderColor: color.button,
                  color: color.button,
                  "&:hover": {
                    color: color.button,
                  },
                }}
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  props.postSubmit();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
